/**
 * Who else tends a garden, and which garden this browser is tending.
 *
 * A person is one account; a garden belongs to one account and may be
 * tended by several. The garden being worked in is remembered on the
 * device and sent with every request as a header, so the backend can put
 * the owner in the caller's place for garden data while leaving profile,
 * billing and the rest personal. The choice is forgotten on signing out,
 * and a refusal of it (no longer a member) leads back to their own garden.
 *
 * The header: `X-Grow-Garden: <owner's user id>` (GARDEN_HEADER), added by
 * apiClient's request interceptor to every request while chosenGarden() is
 * not null, and left off for the person's own garden. It lives in
 * localStorage (CHOSEN_GARDEN_STORAGE_KEY), so it is per device, not per
 * account: forgetChosenGarden runs on sign-out, and a 403 NOT_A_MEMBER
 * answer runs leaveRefusedGarden. Switching reloads the page rather than
 * re-keying the caches (chooseGarden).
 *
 * The postcode: in a shared garden the garden's reads send no ?zip
 * (gardenZip), so the backend judges the owner's plants at the owner's
 * postcode, not the member's own.
 *
 * Endpoints (every answer also carries `success`):
 * - GET    /garden-members → GardenPeople.
 * - POST   /garden-members/invite { email } → { sent, email }; 409
 *   ALREADY_MEMBER or TOO_MANY_INVITES.
 * - GET    /garden-members/invitation?token= → { invitation } (no sign-in).
 * - POST   /garden-members/accept { token } → { ownerId, ownerName, alreadyMember? }.
 * - DELETE /garden-members/:memberId → nothing to read.
 *
 * Who uses it: apiClient (the header), the garden's reads in outlook,
 * plants, crops, plantingDates, today and comingUpRules (gardenZip),
 * AuthContext (forgetting on sign-out), Layout and Header (the switcher, the "you left" note),
 * features/garden-members/JoinGarden, features/tips, user/UserProfile and
 * GardenPeopleCard. Caching: GARDEN_PEOPLE_KEY (['garden', 'people'],
 * profileQueries), invalidated after an invite or a removal.
 *
 * @module gardenMembers
 */

import { apiClient } from './apiClient';
import { CHOSEN_GARDEN_STORAGE_KEY, GARDEN_LEFT_SESSION_KEY } from '@/config/tunables/shared';

/** The request header naming the garden being tended: the backend's contract, not a tunable. */
export const GARDEN_HEADER = 'X-Grow-Garden';
/** Where the chosen garden is remembered on the device. */
const CHOSEN_KEY = CHOSEN_GARDEN_STORAGE_KEY;
/**
 * sessionStorage: the gardener an admin is viewing as (JSON { id, name }), for
 * this tab only (E16; shared/api/viewAs keeps the meaning, this file is the storage helper).
 * @kept K1
 */
const VIEW_AS_SESSION_KEY = 'grow.viewAs';
/** The page an invitation's link opens (JoinGarden), the app's route. */
const JOIN_PATH = '/garden/join';

/**
 * The garden this browser is tending.
 *
 * @returns The owner's user id, or null for the person's own (or when storage cannot be read).
 */
export function chosenGarden(): number | null {
  try {
    const value = window.localStorage.getItem(CHOSEN_KEY);
    const id = value === null ? NaN : parseInt(value, 10);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

/**
 * The postcode to send with a read about the garden being tended (its
 * outlook, plants, crop definitions, planting dates, Today and Coming up).
 *
 * In their own garden it is the person's own postcode, as before. In
 * somebody else's garden none is sent, so the backend reads the garden at
 * its owner's postcode: the member's own would judge the owner's plants by
 * the member's weather and frost dates.
 *
 * @param ownZip - The signed-in person's own postcode (mysqlUser.postal_code), if any.
 * @returns That postcode, trimmed, in their own garden; undefined in a shared garden or without one.
 */
export function gardenZip(ownZip: string | null | undefined): string | undefined {
  if (chosenGarden() !== null) return undefined;
  const zip = ownZip?.trim();
  return zip ? zip : undefined;
}

/**
 * Switches gardens, then reloads.
 *
 * Every query in the app is about one garden, and none of them are keyed
 * by which. Reloading is the honest way to change the answer to that
 * question: cheaper than teaching a hundred caches about it, and it
 * cannot leave one plant's photos beside another garden's plants.
 *
 * @param ownerId - The owner of the garden to tend, or null for the person's own.
 */
export function chooseGarden(ownerId: number | null): void {
  try {
    if (ownerId === null) window.localStorage.removeItem(CHOSEN_KEY);
    else window.localStorage.setItem(CHOSEN_KEY, String(ownerId));
  } catch { /* private mode; it just will not stick */ }
  window.location.assign('/plants');
}

/**
 * Forgets the garden this browser was tending, without reloading.
 *
 * For signing out: the choice is the device's, not the account's, so the
 * next person to sign in here would otherwise send the last one's garden
 * with every request and be refused (403 NOT_A_MEMBER) on every page.
 */
export function forgetChosenGarden(): void {
  try { window.localStorage.removeItem(CHOSEN_KEY); } catch { /* nothing was kept */ }
}

/** Set once a refusal has started the way back, so a burst of refused requests reloads once. */
let leaving = false;

/**
 * The server said this person is not a member of the garden they are
 * tending: they were shown out, they left it in another tab, or someone
 * else signed in on this device. Every garden request would be refused from
 * here on, and the switcher that leads back is gone with the membership, so
 * the app goes back to their own garden and says so (takeGardenLeftNote).
 *
 * Only when a garden is chosen; a refusal of their own garden is not this.
 *
 * @returns True when the way back was started.
 */
export function leaveRefusedGarden(): boolean {
  if (leaving || chosenGarden() === null) return false;
  leaving = true;
  try { window.sessionStorage.setItem(GARDEN_LEFT_SESSION_KEY, '1'); } catch { /* reloads without the note */ }
  // An invitation being opened stays open: it may be to another garden.
  if (window.location.pathname === JOIN_PATH) {
    forgetChosenGarden();
    window.location.reload();
  } else {
    chooseGarden(null);
  }
  return true;
}

/**
 * Whether the last page was sent back here by leaveRefusedGarden, forgetting
 * it as it is read, so the note is said once.
 *
 * @returns True once after such a reload.
 */
export function takeGardenLeftNote(): boolean {
  try {
    if (window.sessionStorage.getItem(GARDEN_LEFT_SESSION_KEY) === null) return false;
    window.sessionStorage.removeItem(GARDEN_LEFT_SESSION_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Somebody invited to the person's own garden. */
export interface GardenMember {
  /** The membership (removeGardenMember takes it). */
  memberId: number;
  /** The address invited. */
  email: string;
  /** Their name once they have an account; null until then. */
  name: string | null;
  /** False while the invitation is still unanswered. */
  accepted: boolean;
  /** True for an invitation not taken up within the days it lasts; inviting the address again renews it. */
  expired: boolean;
  /** When the invitation was (last) sent, as an ISO string in UTC. */
  invitedAt: string;
}

/** A garden somebody else has let this person into. */
export interface SharedGarden {
  /** This person's membership in it (leaving removes it). */
  memberId: number;
  /** The owner's user id: what chooseGarden and X-Grow-Garden take. */
  ownerId: number;
  /** The owner's name, for the switcher. */
  ownerName: string;
  /** When they joined, as the server sends it. */
  joinedAt: string;
}

/** Both sides of sharing: who tends this person's garden, and whose gardens they tend. */
export interface GardenPeople {
  /** People invited to, or already tending, this person's own garden. */
  members: GardenMember[];
  /** Gardens other people have let this person into. */
  gardens: SharedGarden[];
}

/**
 * Who tends this person's garden, and whose gardens they tend.
 *
 * @returns Both lists.
 */
export async function getGardenPeople(): Promise<GardenPeople> {
  const response = await apiClient.get<GardenPeople & { success: boolean }>('/garden-members');
  return response.data;
}

/**
 * Invites somebody, by email, to tend this person's garden. Inviting an
 * address already invited sends it again with a fresh link (and renews an
 * expired one). Refused with 409 ALREADY_MEMBER for somebody who has already
 * joined, and 409 TOO_MANY_INVITES when the garden has as many waiting as it may.
 *
 * @param email - Their address.
 * @returns Whether the email went, and the address as the server kept it.
 */
export async function inviteToGarden(email: string): Promise<{ sent: boolean; email: string }> {
  const response = await apiClient.post<{ success: boolean; sent: boolean; email: string }>('/garden-members/invite', { email });
  return response.data;
}

/** An invitation as its link's page sees it, before anybody signs in. */
export interface Invitation {
  /** Who is inviting. */
  ownerName: string;
  /** The address the invitation was sent to. */
  invitedEmail: string;
  /** True once somebody has taken it up. */
  accepted: boolean;
  /** True when it was not taken up within the days it lasts. */
  expired: boolean;
  /** How many days an invitation lasts from being sent. */
  expiryDays: number;
}

/**
 * Who is asking, before the invitee has signed in.
 *
 * @param token - The invitation's token, from its link.
 * @returns The inviter's name, the address invited, whether it was taken up, and whether it has expired.
 */
export async function readInvitation(token: string): Promise<Invitation> {
  const response = await apiClient.get<{ success: boolean; invitation: Invitation }>(
    '/garden-members/invitation', { params: { token } });
  return response.data.invitation;
}

/**
 * Takes up an invitation, as the signed-in person. Taking up one this person
 * already took up is a success too, with alreadyMember set.
 *
 * @param token - The invitation's token, from its link.
 * @returns The garden's owner, to switch to, and whether they were already in it.
 */
export async function acceptInvitation(token: string): Promise<{ ownerId: number; ownerName: string; alreadyMember: boolean }> {
  const response = await apiClient.post<{ success: boolean; ownerId: number; ownerName: string; alreadyMember?: boolean }>(
    '/garden-members/accept', { token });
  return { ...response.data, alreadyMember: Boolean(response.data.alreadyMember) };
}

/**
 * The owner shows somebody out, or a member leaves.
 *
 * @param memberId - The membership to end.
 * @returns Resolves once it is gone.
 */
export async function removeGardenMember(memberId: number): Promise<void> {
  await apiClient.delete(`/garden-members/${memberId}`);
}

/**
 * Reads the view-as choice as stored (E16).
 *
 * @returns The stored JSON text, or null when none is kept or storage cannot be read.
 */
export function readViewAsStored(): string | null {
  try { return window.sessionStorage.getItem(VIEW_AS_SESSION_KEY); } catch { return null; }
}

/**
 * Keeps, or with null forgets, the view-as choice for this tab (E16).
 *
 * @param value - The JSON text to keep, or null to forget it.
 */
export function writeViewAsStored(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(VIEW_AS_SESSION_KEY);
    else window.sessionStorage.setItem(VIEW_AS_SESSION_KEY, value);
  } catch { /* it will not stick */ }
}
